import { AccountApiTagTypes, AccountApiUrls } from "@/modules/account/common/enum";
import type {
  IAccountHolder,
  IAccountRequest,
  IAccountRequisiteDownloadRequest,
  IAccountRequisitesRequest,
  IAccountsRequest,
  IAccountTransactionsRequest,
  IRenameAccountRequest,
  TAccountRequisitesResponse,
  TAccountResponse,
  TAccountsResponse,
  TAccountTransactionsResponse,
} from "@/modules/account/common/types";
import { ResponseType } from "@/shared/common/enums";
import type {
  IBlobError,
  IBlobUrlResponse,
  IStatementWithIdDownloadRequest,
  IStatementWithIdRequest,
} from "@/shared/common/types";
import { buildRoute, generateQuery } from "@/shared/helpers";
import { blobToText } from "@/shared/utils/common";
import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "app/axios";

export const accountApi = createApi({
  reducerPath: "accountApi",
  baseQuery: axiosBaseQuery(),
  tagTypes: [AccountApiTagTypes.AccountList],
  endpoints: (builder) => ({
    getAccounts: builder.query<TAccountsResponse, IAccountsRequest>({
      query: ({
        pageIndex,
        pageSize,
        accountType,
        accountStatus,
      }) => {
        const query = generateQuery({
          pageIndex,
          pageSize,
          accountType,
          accountStatus,
        });
        const url = `${AccountApiUrls.Accounts}?${query}`;
        return { url };
      },
      providesTags: [AccountApiTagTypes.AccountList],
    }),
    getAccountHolder: builder.query<IAccountHolder, string>({
      query: (accountNumber) => {
        const url = buildRoute(AccountApiUrls.AccountHolder, { accountNumber });
        return { url };
      },
    }),
    getAccountById: builder.query<TAccountResponse, IAccountRequest>({
      query: ({ accountId }) => ({
        url: `${AccountApiUrls.Accounts}/${accountId}`
      })
    }),
    getAccountTransactions: builder.query<
      TAccountTransactionsResponse,
      IAccountTransactionsRequest
    >({
      query: ({
        pageIndex,
        pageSize,
        accountId,
        startDate,
        endDate,
      }) => {
        const query = generateQuery({
          pageIndex,
          pageSize,
          startDate,
          endDate,
        });
        const url = buildRoute(AccountApiUrls.AccountTransactions, { accountId });
        return { url: `${url}?${query}` };
      },
    }),
    renameAccount: builder.mutation<void, IRenameAccountRequest>({
      query: ({ accountNumber, data }) => ({
        url: buildRoute(AccountApiUrls.AccountRename, { accountNumber }),
        method: "PATCH",
        data: data.name,
      })
    }),
    getAccountStatement: builder.query<string, IStatementWithIdRequest>({
      query: ({
        id,
        startDate,
        endDate,
      }) => {
        const query = generateQuery({
          startDate,
          endDate,
        });
        const url = buildRoute(AccountApiUrls.AccountStatement, { accountId: id });
        return {
          url: `${url}?${query}`,
        };
      }
    }),
    downloadAccountStatement: builder.query<
      IBlobUrlResponse,
      IStatementWithIdDownloadRequest
    >({
      query: ({
        id,
        exportFormat,
        startDate,
        endDate,
      }) => {
        const query = generateQuery({
          exportFormat,
          startDate,
          endDate,
        });
        const url = buildRoute(AccountApiUrls.AccountStatementExport, { accountId: id });
        return {
          url: `${url}?${query}`,
          responseType: ResponseType.Blob,
        };
      },
      transformResponse: (blob: Blob) => {
        const url = URL.createObjectURL(blob);
        return { url };
      },
      transformErrorResponse: async (error: IBlobError) => {
        const text = await blobToText(error) as string;
        const data = text ? JSON.parse(text) : null;
        return {
          status: error.status,
          data
        };
      }
    }),
    getAccountRequisites: builder.query<
      TAccountRequisitesResponse,
      IAccountRequisitesRequest
    >({
      query: ({ accountId }) => {
        const url = buildRoute(AccountApiUrls.AccountRequisite, { accountId });
        return { url };
      }
    }),
    downloadAccountRequisite: builder.query<
      IBlobUrlResponse,
      IAccountRequisiteDownloadRequest
    >({
      query: ({ accountId }) => {
        const url = buildRoute(AccountApiUrls.AccountRequisiteExport, { accountId });
        return {
          url,
          responseType: ResponseType.Blob,
        };
      },
      transformResponse: (blob: Blob) => {
        const url = URL.createObjectURL(blob);
        return { url };
      },
      transformErrorResponse: async (error: IBlobError) => {
        const text = await blobToText(error) as string;
        const data = text ? JSON.parse(text) : null;
        return {
          status: error.status,
          data
        };
      }
    }),
  }),
});

export const {
  useGetAccountsQuery,
  useGetAccountByIdQuery,
  useRenameAccountMutation,
  useGetAccountTransactionsQuery,
  useGetAccountStatementQuery,
  useLazyDownloadAccountStatementQuery,
  useGetAccountRequisitesQuery,
  useLazyGetAccountHolderQuery,
  useLazyDownloadAccountRequisiteQuery,
} = accountApi;
